import EnquiryProductSummary from '../components/EnquiryProductSummary';
import EnquiryInterest from '../components/EnquiryInterest';
import { hasAiAccess } from '../utils/aiAccess';
import TaskSource from '../components/TaskSource';
import { can } from '../utils/memberPermissions';
import ConversationTags from '../components/ConversationTags';
import WhatsAppLeadCapture from '../components/whatsapp/WhatsAppLeadCapture';
import { LeadIndustryDetails } from '../components/IndustryFields';
import LeadDeal from '../components/LeadDeal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, Plus, Trash2, Edit2, Check, X, Clock, UserPlus, Mail, Building2, Package, Wallet, Zap, Users, ChevronDown, Phone, UserRound } from 'lucide-react';
import { format, parseISO, formatDistanceToNow, isToday } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activityAPI, leadsAPI, notesAPI, followUpsAPI, workspaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import FollowUpForm from '../components/FollowUpForm';
import FollowUpActions from '../components/FollowUpActions';
import FormError from '../components/FormError';
import { formatFollowUp } from '../utils/followUps';
import { STATUS_OPTIONS, getLeadStatus } from '../utils/leadStatuses';

const captureHeading = /^Captured from WhatsApp(?:\r?\n|$)/;
const isWhatsAppNote = note => note.source === 'whatsapp' || captureHeading.test(note.content || '');
const noteText = note => (note.content || '').replace(captureHeading, '');

const DetailSection = ({ id, title, description, icon: Icon, children }) => (
  <details className="group mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white open:border-primary-200">
    <summary aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
      className="flex min-h-[76px] cursor-pointer list-none items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600 group-open:rounded-b-none group-open:bg-primary-50 sm:px-4 [&::-webkit-details-marker]:hidden">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 group-open:bg-white group-open:text-primary-700">
        <Icon aria-hidden="true" className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span id={`${id}-title`} className="block text-sm font-semibold text-gray-900">{title}</span>
        <span id={`${id}-description`} className="mt-0.5 block text-xs leading-5 text-gray-600">{description}</span>
      </span>
      <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500 transition-transform group-open:rotate-180 group-open:text-primary-700 motion-reduce:transition-none" />
    </summary>
    <div className="space-y-3 border-t border-gray-200 bg-gray-50/70 px-3 py-4 sm:px-4">{children}</div>
  </details>
);

const SOURCE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const LEGACY_STATUS_LABELS = { interested: 'Contacted' };
const formatStatusValue = (value) => {
  if (!value) return '';
  const normalized = typeof value === 'string' ? value.toLowerCase() : value;
  return getLeadStatus(normalized).label
    || LEGACY_STATUS_LABELS[normalized]
    || value;
};

const friendlyError = (err, fallback) => {
  const raw = err?.response?.data?.error || err?.response?.data?.errors?.[0]?.msg || err?.message || fallback;
  if (typeof raw !== 'string') return fallback;
  const lower = raw.toLowerCase();
  if (
    lower.includes('column') ||
    lower.includes('schema') ||
    lower.includes('migration') ||
    lower.includes('supabase') ||
    lower.includes('sql editor') ||
    lower.includes('database/')
  ) {
    return 'This could not be saved right now. Please try again or contact support.';
  }
  return raw;
};

const formatAssignmentValue = (value) => (value ? 'Assigned' : 'Unassigned');

const getActivityLabel = (entry) => {
  const metadata = entry?.metadata || {};
  switch (entry?.action) {
    case 'note_added':
      return 'Added a note';
    case 'note_edited':
      return 'Edited a note';
    case 'lead_created':
      return 'Created enquiry';
    case 'lead_updated':
      return metadata.lastWhatsappContactAt ? 'Marked WhatsApp contact' : 'Updated enquiry';
    case 'status_change':
      return 'Changed status';
    case 'lead_reassigned':
      return 'Reassigned enquiry';
    case 'follow_up_created':
      return 'Scheduled follow-up';
    case 'follow_up_completed':
      return 'Completed follow-up';
    default:
      return entry?.action ? entry.action.replace(/_/g, ' ') : 'Activity';
  }
};

const getActivityMeta = (entry) => {
  const metadata = entry?.metadata || {};
  switch (entry?.action) {
    case 'status_change':
      return `${formatStatusValue(metadata.from)} → ${formatStatusValue(metadata.to)}`.trim();
    case 'lead_reassigned':
      return `From ${metadata.fromAgentId ? 'AI agent' : formatAssignmentValue(metadata.from)} to ${metadata.assignedAgentId ? 'AI agent' : formatAssignmentValue(metadata.to)}`;
    case 'follow_up_completed':
    case 'follow_up_created': {
      const when = [metadata.date, metadata.time].filter(Boolean).join(' ');
      return [when, metadata.note].filter(Boolean).join(' — ') || null;
    }
    default:
      return null;
  }
};

const LeadDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  return <LeadDetailContent key={JSON.stringify([user?.id, user?.workspaceOwnerId, id])} />;
};

// Each enquiry owns its editor state, including requests that finish after navigation.
const LeadDetailContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('notes');
  const [confirmation, setConfirmation] = useState('');
  const [actionError, setActionError] = useState('');

  const [lastMessageSummaryDraft, setLastMessageSummaryDraft] = useState('');
  const [isLastMessageSummaryDirty, setIsLastMessageSummaryDirty] = useState(false);
  const [savingLastMessageSummary, setSavingLastMessageSummary] = useState(false);
  const [lastMessageSummaryError, setLastMessageSummaryError] = useState('');
  const [isEditingIdentity, setIsEditingIdentity] = useState(false);
  const [identityDraft, setIdentityDraft] = useState({ name: '', phoneNumber: '', socialHandle: '', email: '', companyName: '', source: '', referrerName: '' });
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityError, setIdentityError] = useState('');
  const [isEditingIntent, setIsEditingIntent] = useState(false);
  const [intentDraft, setIntentDraft] = useState({
    productOrService: '',
    budgetRange: '',
    urgency: '',
  });
  const [savingIntent, setSavingIntent] = useState(false);
  const [intentError, setIntentError] = useState('');
  const [markingContacted, setMarkingContacted] = useState(false);
  const [whatsAppContextError, setWhatsAppContextError] = useState('');
  const [contactConfirmation, setContactConfirmation] = useState('');

  // Notes state
  const [backgroundDraft, setBackgroundDraft] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteError, setNoteError] = useState('');
  const [noteConfirmation, setNoteConfirmation] = useState('');

  // Mention typeahead state
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionCursorIdx, setMentionCursorIdx] = useState(-1);
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);
  const noteTextareaRef = useRef(null);

  // Follow-up state
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [followUpConfirmation, setFollowUpConfirmation] = useState('');
  const followUpsPanelRef = useRef(null);
  const tabRefs = useRef({});
  const focusFollowUpsRef = useRef(false);


  const {
    data: lead,
    isLoading: loading,
    isError,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      const { data } = await leadsAPI.getById(id);
      return data;
    },
    enabled: !!id,
  });

  const { data: leadViewers = [] } = useQuery({
    queryKey: ['leadViewers', id],
    queryFn: async () => {
      const { data } = await leadsAPI.getViewers(id);
      return data || [];
    },
    enabled: !!id,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  const error = isError ? 'Could not load enquiry' : null;
  const isOwner = user?.role === 'owner';
  const teamInboxEnabled = Boolean(user?.teamInboxEnabled);

  useEffect(() => {
    // When switching leads, reset draft state.
    setIsLastMessageSummaryDirty(false);
    setConfirmation('');
    setActionError('');
    setContactConfirmation('');
    setNoteError('');
    setNoteConfirmation('');
    setFollowUpConfirmation('');
  }, [id]);

  useEffect(() => {
    if (!location.state?.leadCreated) return;
    setConfirmation('Enquiry added.');
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (activeTab === 'followups' && focusFollowUpsRef.current) {
      followUpsPanelRef.current?.focus();
      focusFollowUpsRef.current = false;
    }
  }, [activeTab]);

  const selectTab = (tab) => {
    setActiveTab(tab);
  };

  const handleTabKeyDown = (event) => {
    const tabs = ['notes', 'followups', 'activity'];
    const currentIndex = tabs.indexOf(activeTab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    setActiveTab(tabs[nextIndex]);
    setTimeout(() => tabRefs.current[tabs[nextIndex]]?.focus(), 0);
  };

  const manageFollowUps = ({ openForm = false } = {}) => {
    focusFollowUpsRef.current = activeTab !== 'followups';
    if (activeTab === 'followups') followUpsPanelRef.current?.focus();
    setActiveTab('followups');
    if (openForm) setShowAddFollowUp(true);
  };

  useEffect(() => {
    if (isLastMessageSummaryDirty) return;
    setLastMessageSummaryDraft(lead?.lastMessageSummary || '');
  }, [lead?.lastMessageSummary, isLastMessageSummaryDirty]);

  useEffect(() => {
    if (!isEditingIdentity && lead) {
      setIdentityDraft({
        name: lead.name || '',
        phoneNumber: lead.phoneNumber || '',
        socialHandle: lead.socialHandle || '',
        email: lead.email || '',
        companyName: lead.companyName || '',
        source: lead.source || '',
        otherSource: lead.otherSource || '',
        referrerName: lead.referrerName || '',
      });
    }
  }, [lead, isEditingIdentity]);

  useEffect(() => {
    if (!isEditingIntent && lead) {
      setIntentDraft({
        productOrService: lead.productOrService || '',
        catalogueItemId: lead.catalogueItemId || null,
        budgetRange: lead.budgetRange || '',
        urgency: lead.urgency || '',
      });
    }
  }, [lead, isEditingIntent]);

  useEffect(() => {
    if (!id) return undefined;

    let isActive = true;
    const sendHeartbeat = async () => {
      if (!isActive) return;
      try {
        await leadsAPI.postViewing(id);
      } catch (err) {
        // Heartbeat errors shouldn't block lead viewing.
      }
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 15000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [id]);

  const updateStatusMutation = useMutation({
    mutationFn: (status) => leadsAPI.update(id, { status }),
    onMutate: async (status) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      queryClient.setQueryData(['lead', id], (old) => (old ? { ...old, status } : old));
      return { previous };
    },
    onError: (_err, _status, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
    },
  });

  const handleStatusChange = async (newStatus) => {
    try {
      setConfirmation('');
      setActionError('');
      await updateStatusMutation.mutateAsync(newStatus);
      setConfirmation(`Enquiry status saved: ${getLeadStatus(newStatus).label}.`);
    } catch (err) {
      console.error('Failed to update status:', err);
      setConfirmation('');
      setActionError(friendlyError(err, 'Could not update status. Please try again.'));
    }
  };

  const updateIdentityMutation = useMutation({
    mutationFn: (payload) => leadsAPI.update(id, payload),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['lead', id], (old) => old ? { ...old, ...data } : old);
      setIsEditingIdentity(false);
      setIdentityError('');
      setConfirmation('Contact details saved.');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      ['contacts', 'organisations', 'lead', 'leads', 'inbox', 'dashboard', 'followUps', 'activity'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
  });

  const handleSaveIdentity = async () => {
    setIdentityError('');
    if (!identityDraft.name.trim() || ![identityDraft.phoneNumber, identityDraft.email, identityDraft.socialHandle].some(value => value?.trim())) {
      setIdentityError('Name and at least one contact method are required.');
      return;
    }
    setSavingIdentity(true);
    try {
      const payload = {
        name: identityDraft.name.trim(),
        phoneNumber: identityDraft.phoneNumber.trim() || null,
        socialHandle: identityDraft.socialHandle?.trim() || null,
        email: identityDraft.email?.trim() || null,
        source: identityDraft.source?.trim() || null,
        otherSource: identityDraft.source === 'other' ? identityDraft.otherSource?.trim() || null : null,
        referrerName: identityDraft.source === 'referral' ? identityDraft.referrerName?.trim() || null : null,
      };
      await updateIdentityMutation.mutateAsync(payload);
    } catch (err) {
      setIdentityError(friendlyError(err, 'Could not save contact details. Please try again.'));
    } finally {
      setSavingIdentity(false);
    }
  };

  const updateIntentMutation = useMutation({
    mutationFn: (payload) => leadsAPI.update(id, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      queryClient.setQueryData(['lead', id], (old) => (old ? { ...old, ...payload } : old));
      return { previous };
    },
    onSuccess: () => {
      setIsEditingIntent(false);
      setIntentError('');
      setConfirmation('Intent details saved.');
    },
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
    },
  });

  const backgroundMutation = useMutation({
    mutationFn: (text) => leadsAPI.update(id, { variantSpecs: text.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setBackgroundDraft(null);
      setConfirmation('Requirements / notes saved.');
    },
  });

  const handleSaveIntent = async () => {
    setIntentError('');
    setSavingIntent(true);
    try {
      const payload = {
        productOrService: intentDraft.productOrService?.trim() || null,
        catalogueItemId: intentDraft.catalogueItemId || null,
        budgetRange: intentDraft.budgetRange?.trim() || null,
        urgency: intentDraft.urgency?.trim() || null,
      };
      await updateIntentMutation.mutateAsync(payload);
    } catch (err) {
      setIntentError(friendlyError(err, 'Could not save intent details. Please try again.'));
    } finally {
      setSavingIntent(false);
    }
  };

  const saveLastMessageSummaryMutation = useMutation({
    mutationFn: (summaryToSave) => leadsAPI.update(id, { lastMessageSummary: summaryToSave }),
    onMutate: async (summaryToSave) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      const trimmed = summaryToSave.trim();
      queryClient.setQueryData(['lead', id], (old) =>
        old ? { ...old, lastMessageSummary: trimmed || null } : old
      );
      return { previous };
    },
    onSuccess: (_res, summaryToSave) => {
      setIsLastMessageSummaryDirty(false);
      setLastMessageSummaryDraft(summaryToSave);
    },
    onError: (_err, _summary, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
    },
  });

  const handleSaveLastMessageSummary = async (nextSummary) => {
    const summaryToSave = typeof nextSummary === 'string' ? nextSummary : lastMessageSummaryDraft;
    try {
      setLastMessageSummaryError('');
      setSavingLastMessageSummary(true);
      await saveLastMessageSummaryMutation.mutateAsync(summaryToSave);
      setConfirmation('Last message summary saved.');
    } catch (err) {
      console.error('Failed to update last message summary:', err);
      setLastMessageSummaryError(friendlyError(err, 'Could not save last message summary. Please try again.'));
    } finally {
      setSavingLastMessageSummary(false);
    }
  };

  const markContactedMutation = useMutation({
    mutationFn: async () => {
      const { data } = await leadsAPI.markWhatsappContactNow(id);
      return data?.lastWhatsappContactAt || new Date().toISOString();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      queryClient.setQueryData(['lead', id], (old) =>
        old ? { ...old, lastWhatsappContactAt: new Date().toISOString() } : old
      );
      return { previous };
    },
    onSuccess: (lastWhatsappContactAt) => {
      queryClient.setQueryData(['lead', id], (old) =>
        old ? { ...old, lastWhatsappContactAt } : old
      );
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
    },
  });

  const handleMarkContactedToday = async () => {
    try {
      setWhatsAppContextError('');
      setMarkingContacted(true);
      await markContactedMutation.mutateAsync();
      setContactConfirmation('Contact logged. Sales stage and follow-up tasks are unchanged.');
    } catch (err) {
      console.error('Failed to mark contacted today:', err);
      setWhatsAppContextError(friendlyError(err, 'Could not log contact. Please try again.'));
    } finally {
      setMarkingContacted(false);
    }
  };

  const addNoteMutation = useMutation({
    mutationFn: (content) => notesAPI.create({ leadId: id, content }),
    onMutate: async (content) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      const optimisticNote = {
        id: `temp-${Date.now()}`,
        content,
        createdAt: new Date().toISOString(),
        authorName: user?.fullName || 'You',
      };
      queryClient.setQueryData(['lead', id], (old) => {
        if (!old) return old;
        return { ...old, notes: [optimisticNote, ...(old.notes || [])] };
      });
      return { previous };
    },
    onError: (_err, _content, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['lead', id] }),
  });

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setNoteError('');
      setNoteConfirmation('');
      setAddingNote(true);
      await addNoteMutation.mutateAsync(newNote);
      setNewNote('');
      setNoteConfirmation('Note added.');
    } catch (err) {
      console.error('Failed to add note:', err);
      setNoteError(friendlyError(err, 'Could not add note. Please try again.'));
    } finally {
      setAddingNote(false);
    }
  };

  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, content }) => notesAPI.update(noteId, content),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead', id] }),
  });

  const handleUpdateNote = async (noteId, content) => {
    try {
      setNoteError('');
      setNoteConfirmation('');
      await updateNoteMutation.mutateAsync({ noteId, content });
      setEditingNote(null);
      setNoteConfirmation('Note saved.');
    } catch (err) {
      console.error('Failed to update note:', err);
      setEditingNote({ id: noteId, content });
      setNoteError(friendlyError(err, 'Could not save note. Please try again.'));
    }
  };

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => notesAPI.delete(noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      queryClient.setQueryData(['lead', id], (old) => {
        if (!old) return old;
        return { ...old, notes: (old.notes || []).filter((n) => n.id !== noteId) };
      });
      return { previous };
    },
    onError: (_err, _noteId, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['lead', id] }),
  });

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      setNoteError('');
      setNoteConfirmation('');
      await deleteNoteMutation.mutateAsync(noteId);
      setNoteConfirmation('Note deleted.');
    } catch (err) {
      console.error('Failed to delete note:', err);
      setNoteError(friendlyError(err, 'Could not delete note. Please try again.'));
    }
  };

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['workspace', 'members'],
    queryFn: async () => {
      const { data } = await workspaceAPI.getMembers();
      return data || [];
    },
    enabled: teamInboxEnabled,
  });

  const { data: agentTeam } = useQuery({
    queryKey: ['workspace', 'agents', user?.workspaceOwnerId || user?.id],
    queryFn: async () => (await workspaceAPI.getAgents()).data,
    enabled: hasAiAccess(user) && Boolean(user?.whatsappInboxEnabled), retry: false,
  });
  const teamAgents = hasAiAccess(user) ? agentTeam?.agents || [] : [];
  const assignmentMembers = [...teamMembers];
  if (isOwner && !assignmentMembers.some(member => member.userId === user?.id)) assignmentMembers.unshift({ userId: user.id, fullName: user.fullName || 'You', role: 'owner' });
  // Mention helpers
  const mentionFilteredMembers = teamMembers.filter((m) => {
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return (m.fullName || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  }).slice(0, 5);

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNewNote(value);

    if (!teamInboxEnabled) return;

    const cursorPos = e.target.selectionStart;
    // Find the @ trigger: scan backwards from cursor
    const textBefore = value.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx >= 0) {
      const between = textBefore.slice(atIdx + 1);
      // Only activate if no spaces in query (simple check) and @ is at start or preceded by whitespace
      if (!/\s/.test(between) && (atIdx === 0 || /\s/.test(textBefore[atIdx - 1]))) {
        setMentionActive(true);
        setMentionQuery(between);
        setMentionCursorIdx(atIdx);
        setMentionSelectedIdx(0);
        return;
      }
    }
    setMentionActive(false);
    setMentionQuery('');
    setMentionCursorIdx(-1);
  };

  const insertMention = (member) => {
    const token = `@[${member.fullName || member.email}](${member.userId})`;
    const before = newNote.slice(0, mentionCursorIdx);
    const after = newNote.slice(noteTextareaRef.current?.selectionStart || (mentionCursorIdx + 1 + mentionQuery.length));
    const updatedNote = before + token + ' ' + after;
    setNewNote(updatedNote);
    setMentionActive(false);
    setMentionQuery('');
    setMentionCursorIdx(-1);

    // Refocus textarea
    setTimeout(() => {
      if (noteTextareaRef.current) {
        const newPos = before.length + token.length + 1;
        noteTextareaRef.current.focus();
        noteTextareaRef.current.selectionStart = newPos;
        noteTextareaRef.current.selectionEnd = newPos;
      }
    }, 0);
  };

  const handleNoteKeyDown = (e) => {
    if (!mentionActive || mentionFilteredMembers.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionSelectedIdx((prev) => Math.min(prev + 1, mentionFilteredMembers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(mentionFilteredMembers[mentionSelectedIdx]);
    } else if (e.key === 'Escape') {
      setMentionActive(false);
    }
  };

  const addFollowUpMutation = useMutation({
    mutationFn: (payload) => followUpsAPI.create(payload),
    onSuccess: async () => {
      await Promise.all(['lead', 'dashboard', 'leads', 'inbox', 'followUps', 'activity'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key], refetchType: key === 'dashboard' ? 'all' : 'active' })
      ));
    },
  });

  const assignMutation = useMutation({
    mutationFn: (assignee) => {
      if (isOwner && agentTeam?.enabled && (!teamInboxEnabled || assignee?.startsWith('agent:') || lead.assignedAgentId)) return workspaceAPI.assignAgent({ target: 'lead', id, ...(assignee?.startsWith('agent:') ? { agentId: assignee.slice(6) } : { userId: assignee || null }) });
      return leadsAPI.assign(id, assignee);
    },
    onMutate: async (assignedUserId) => {
      await queryClient.cancelQueries({ queryKey: ['lead', id] });
      const previous = queryClient.getQueryData(['lead', id]);
      const assignedAgentId = assignedUserId?.startsWith('agent:') ? assignedUserId.slice(6) : null;
      const humanId = assignedAgentId ? null : assignedUserId;
      const assignedMember = teamMembers.find((m) => m.userId === humanId);
      queryClient.setQueryData(['lead', id], (old) =>
        old ? { ...old, assignedUserId: humanId, assignedAgentId, assignedUser: assignedMember || null } : old
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['lead', id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  const { data: followUpHistory = [], dataUpdatedAt: followUpsUpdatedAt, isLoading: loadingFollowUps, isError: followUpsError } = useQuery({
    queryKey: ['followUps', id],
    queryFn: async () => {
      const { data } = await followUpsAPI.getByLead(id);
      return data;
    },
    enabled: activeTab === 'followups',
  });
  const activeFollowUp = followUpsUpdatedAt && !followUpsError
    ? followUpHistory.find((item) => (item.status || (item.cancelled ? 'cancelled' : item.completed ? 'completed' : 'open')) === 'open')
    : lead?.activeFollowUp;

  const { data: activityLog = [] } = useQuery({
    queryKey: ['activity', id],
    queryFn: async () => {
      const { data } = await activityAPI.getByLead(id);
      return data || [];
    },
    enabled: activeTab === 'activity',
  });

  const { data: watchers = [] } = useQuery({
    queryKey: ['leadWatchers', id],
    queryFn: async () => {
      const { data } = await leadsAPI.getWatchers(id);
      return data || [];
    },
    enabled: !!id && teamInboxEnabled,
  });

  const isWatching = watchers.some((w) => w.userId === user?.id);

  const watchMutation = useMutation({
    mutationFn: () => leadsAPI.addWatcher(id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['leadWatchers', id] });
      const previous = queryClient.getQueryData(['leadWatchers', id]);
      queryClient.setQueryData(['leadWatchers', id], (old) => [
        ...(old || []),
        { userId: user?.id, fullName: user?.fullName || 'You' },
      ]);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['leadWatchers', id], ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['leadWatchers', id] }),
  });

  const unwatchMutation = useMutation({
    mutationFn: () => leadsAPI.removeWatcher(id, user?.id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['leadWatchers', id] });
      const previous = queryClient.getQueryData(['leadWatchers', id]);
      queryClient.setQueryData(['leadWatchers', id], (old) =>
        (old || []).filter((w) => w.userId !== user?.id)
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['leadWatchers', id], ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['leadWatchers', id] }),
  });

  // Editing indicator
  const { data: leadEditors = [] } = useQuery({
    queryKey: ['leadEditors', id],
    queryFn: async () => {
      const { data } = await leadsAPI.getEditors(id);
      return data || [];
    },
    enabled: !!id && teamInboxEnabled,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });

  const editingIntervalRef = useRef(null);
  const isEditingRef = useRef(false);

  const startEditingHeartbeat = useCallback((section = 'general') => {
    if (!id || !teamInboxEnabled) return;
    isEditingRef.current = true;

    // Send first heartbeat immediately
    leadsAPI.postEditing(id, section).catch(() => { });

    // Clear existing interval if any
    if (editingIntervalRef.current) {
      clearInterval(editingIntervalRef.current);
    }

    editingIntervalRef.current = setInterval(() => {
      if (!isEditingRef.current) return;
      leadsAPI.postEditing(id, section).catch(() => { });
    }, 15000);
  }, [id, teamInboxEnabled]);

  const stopEditingHeartbeat = useCallback(() => {
    isEditingRef.current = false;
    if (editingIntervalRef.current) {
      clearInterval(editingIntervalRef.current);
      editingIntervalRef.current = null;
    }
    if (id) {
      leadsAPI.stopEditing(id).catch(() => { });
    }
  }, [id]);

  // Fire/stop editing heartbeat when entering/leaving edit mode
  useEffect(() => {
    if (isEditingIdentity || isEditingIntent) {
      startEditingHeartbeat(isEditingIdentity ? 'identity' : 'intent');
    } else {
      stopEditingHeartbeat();
    }
  }, [isEditingIdentity, isEditingIntent, startEditingHeartbeat, stopEditingHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editingIntervalRef.current) {
        clearInterval(editingIntervalRef.current);
      }
      if (isEditingRef.current && id) {
        leadsAPI.stopEditing(id).catch(() => { });
      }
    };
  }, [id]);

  const otherEditors = (leadEditors || []).filter((e) => e.id !== user?.id);
  const editorNames = otherEditors.map((e) => e.fullName || 'Team member');
  let editingLabel = '';
  if (editorNames.length === 1) {
    editingLabel = `${editorNames[0]} is editing this enquiry`;
  } else if (editorNames.length === 2) {
    editingLabel = `${editorNames[0]} and ${editorNames[1]} are editing this enquiry`;
  } else if (editorNames.length > 2) {
    editingLabel = `${editorNames[0]}, ${editorNames[1]} and ${editorNames.length - 2} others are editing this enquiry`;
  }


  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteCancelRef = useRef(null);
  const deleteTriggerRef = useRef(null);
  useEffect(() => {
    if (confirmDelete) deleteCancelRef.current?.focus();
  }, [confirmDelete]);
  const deleteLeadMutation = useMutation({
    mutationFn: () => leadsAPI.delete(id),
    onSuccess: () => {
      ['leads', 'lead', 'salesReport', 'dashboard', 'followUps', 'contacts', 'organisations', 'whatsapp'].forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      navigate('/leads');
    },
  });

  const cancelDelete = () => {
    if (deleteLeadMutation.isPending) return;
    setConfirmDelete(false);
    deleteLeadMutation.reset();
    deleteTriggerRef.current?.focus();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse rounded-lg overflow-hidden bg-white border border-gray-100">
          <div className="h-1.5 bg-gray-200" />
          <div className="p-5 space-y-4">
            <div className="h-8 w-48 bg-gray-200 rounded-lg" />
            <div className="h-5 w-36 bg-gray-100 rounded" />
            <div className="h-24 bg-gray-100 rounded-lg" />
          </div>
        </div>
        <div className="h-48 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="p-4">
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 font-medium">
          {error || 'Lead not found'}
        </div>
      </div>
    );
  }

  const lastContactLabel = (() => {
    if (!lead.lastWhatsappContactAt) return 'Never';
    try {
      const date = parseISO(lead.lastWhatsappContactAt);
      if (isToday(date)) return 'Today';
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '—';
    }
  })();

  const lastContactExact = (() => {
    if (!lead.lastWhatsappContactAt) return null;
    try {
      return format(parseISO(lead.lastWhatsappContactAt), 'MMM d, yyyy h:mm a');
    } catch {
      return null;
    }
  })();

  const otherViewers = (leadViewers || []).filter((viewer) => viewer.id !== user?.id);
  const viewerNames = otherViewers.map((viewer) => viewer.fullName || viewer.email || 'Team member');
  let viewerLabel = '';
  if (viewerNames.length === 1) {
    viewerLabel = viewerNames[0];
  } else if (viewerNames.length === 2) {
    viewerLabel = `${viewerNames[0]} and ${viewerNames[1]}`;
  } else if (viewerNames.length > 2) {
    viewerLabel = `${viewerNames[0]}, ${viewerNames[1]} and ${viewerNames.length - 2} others`;
  }

  return (
    <div className="space-y-5 lg:space-y-6">
      {confirmation && (
        <div role="status" aria-live="polite" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-800">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span>{confirmation}</span>
            {confirmation === 'Enquiry added.' && (
              <button
                type="button"
                onClick={() => manageFollowUps({ openForm: true })}
                className="min-h-[44px] text-left font-semibold text-green-900 underline underline-offset-2"
              >
                Add task
              </button>
            )}
          </div>
        </div>
      )}
      {actionError && <FormError message={actionError} />}
      {/* Lead Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight break-words">{lead.title || lead.productOrService || 'Sales enquiry'}</h2>
              <p className="text-gray-600">{lead.name}</p>
              {lead.phoneNumber && (<a
                href={`tel:${lead.phoneNumber?.replace(/\s/g, '')}`}
                className="mt-1 inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700"
              >
                {lead.phoneNumber}
              </a>)}
              {lead.socialHandle && <p className="mt-2 text-sm text-gray-600 break-words">{lead.socialHandle}</p>}
              {(lead.email || lead.companyName) && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-sm">
                  {lead.email && (
                    <span className="flex min-w-0 items-center gap-2 text-gray-600">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                        <Mail className="w-4 h-4" />
                      </span>
                      <span className="min-w-0 break-all">{lead.email}</span>
                    </span>
                  )}
                  {lead.companyName && (
                    <span className="flex min-w-0 items-center gap-2 text-gray-600">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                        <Building2 className="w-4 h-4" />
                      </span>
                      <span className="min-w-0 break-words">{lead.companyName}</span>
                    </span>
                  )}

                </div>
              )}
              {viewerLabel && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                  Viewed by {viewerLabel}
                </p>
              )}
              {editingLabel && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                  {editingLabel}
                </p>
              )}
            </div>
            <div className="max-w-[42%] shrink-0">
              <p className="mb-1 text-xs font-medium text-gray-500">Sales stage</p>
            <select
              aria-label="Enquiry status"
              disabled={!can(user, 'editEnquiries') || updateStatusMutation.isPending || (teamInboxEnabled && !isOwner && lead.assignedUserId !== user?.id)}
              value={lead.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className={`w-full min-h-[44px] px-2 py-2 rounded-lg text-sm font-medium border-0 cursor-pointer shadow-sm ${getLeadStatus(lead.status).className}`}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            </div>
          </div>

          {updateStatusMutation.isPending && <p role="status" className="mb-3 text-sm text-gray-600">Saving status…</p>}

          {user?.whatsappInboxEnabled && <WhatsAppLeadCapture leadId={lead.id} linkOnly />}
          <section aria-label="Enquiry overview" className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3.5 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">At a glance</h3>
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <EnquiryProductSummary lead={lead}/>
              <div className="flex min-w-0 gap-2.5 rounded-lg border border-gray-200 bg-white p-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Wallet aria-hidden="true" className="h-4 w-4" /></span>
                <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Potential value</dt>
                <dd className="mt-0.5 text-sm font-semibold text-gray-900 break-words">
                  {lead.dealValue == null ? 'Not estimated yet' : `${lead.currency || 'USD'} ${Number(lead.dealValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </dd></span>
              </div>
              <div className="flex min-w-0 gap-2.5 rounded-lg border border-gray-200 bg-white p-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700"><Zap aria-hidden="true" className="h-4 w-4" /></span>
                <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Buying timeframe</dt>
                <dd className="mt-0.5 text-sm font-semibold text-gray-900 break-words">{lead.urgency ? ({ now: 'Ready now', soon: 'Soon', browsing: 'Just browsing' }[lead.urgency] || lead.urgency) : 'Not recorded yet'}</dd></span>
              </div>
              <div className="flex min-w-0 gap-2.5 rounded-lg border border-gray-200 bg-white p-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Users aria-hidden="true" className="h-4 w-4" /></span>
                <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Responsible</dt>
                <dd className="mt-0.5 text-sm font-semibold text-gray-900 break-words">{hasAiAccess(user) && lead.assignedAgentId ? `${teamAgents.find(a => a.id === lead.assignedAgentId)?.name || 'AI agent'} (AI)` : lead.assignedUser?.fullName || lead.assignedUser?.businessName || (lead.assignedUserId ? 'Team member' : 'Unassigned')}</dd></span>
              </div>
              {lead.recurringValue != null && <div className="rounded-lg border border-gray-200 bg-white p-3"><dt className="text-xs font-medium text-gray-500">Recurring fee</dt><dd className="mt-0.5 text-sm font-semibold text-gray-900">{lead.currency || 'USD'} {Number(lead.recurringValue).toLocaleString(undefined, { minimumFractionDigits: 2 })} / {lead.billingInterval}</dd></div>}
            </dl>
            {(lead.variantSpecs || backgroundDraft !== null) && (
              <section aria-label="Requirements / notes" className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-xs font-medium text-gray-500">Requirements / notes</h4>
                  {isOwner && backgroundDraft === null && <button type="button" aria-label="Edit requirements and notes"
                    className="min-h-[36px] px-2 text-xs font-semibold text-primary-700"
                    onClick={() => { backgroundMutation.reset(); setBackgroundDraft(lead.variantSpecs || ''); }}>Edit</button>}
                </div>
                {backgroundDraft === null ? <p className="text-sm font-medium leading-5 text-gray-900 whitespace-pre-wrap break-words">{lead.variantSpecs}</p> : (
                  <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); backgroundMutation.mutate(backgroundDraft); }}>
                    <label htmlFor="requirements-notes" className="sr-only">Requirements / notes</label>
                    <textarea id="requirements-notes" value={backgroundDraft} maxLength={300} rows={3} disabled={backgroundMutation.isPending}
                      onChange={(event) => setBackgroundDraft(event.target.value)} className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm" />
                    {backgroundMutation.isError && <FormError message={friendlyError(backgroundMutation.error, 'Could not save requirements / notes. Please try again.')} />}
                    <div className="flex gap-3">
                      <button type="submit" disabled={backgroundMutation.isPending} className="min-h-[40px] rounded-lg bg-primary-600 px-3 text-sm font-medium text-white">{backgroundMutation.isPending ? 'Saving...' : 'Save requirements'}</button>
                      <button type="button" disabled={backgroundMutation.isPending} onClick={() => setBackgroundDraft(null)} className="min-h-[40px] px-3 text-sm text-gray-600">Cancel</button>
                    </div>
                  </form>
                )}
              </section>
            )}
            <div className="rounded-lg border border-primary-100 bg-primary-50 px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium text-gray-600">Next task</p>
                {activeFollowUp && <button type="button" onClick={() => manageFollowUps()}
                  className="min-h-[44px] text-sm font-semibold text-primary-700">Manage task</button>}
              </div>
              <p className="text-sm font-semibold text-gray-900">
                {activeFollowUp ? formatFollowUp(activeFollowUp) : 'No task scheduled'}
              </p>
              {activeFollowUp?.note && <p className="mt-1 text-sm text-gray-600 break-words">{activeFollowUp.note}</p>}
            </div>
          </section>

          <div className="flex gap-3">
            {lead.whatsappUrl && (<a
              href={lead.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[44px] flex-1 flex items-center justify-center gap-2 bg-whatsapp-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-whatsapp-600 active:bg-whatsapp-700 transition-all shadow-sm whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Open WhatsApp
            </a>)}
            {!lead.whatsappUrl && lead.email && <a href={`mailto:${lead.email}`} className="min-h-[44px] flex-1 flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700">Email</a>}
            <button type="button" disabled={!can(user, 'manageFollowUps')} onClick={() => manageFollowUps({ openForm: true })}
              className="min-h-[44px] flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700">
              Add task
            </button>

          </div>

          <DetailSection id="enquiry-contact" title="Contact & deal" description="People, requirements and pricing" icon={Users}>
          {/* Identity & Source (editable by owner only per RULES) */}
          {isOwner && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span><span className="block text-sm font-semibold text-gray-900">Shared contact details</span><span className="mt-0.5 block text-xs text-gray-500">Name, phone and contact source</span></span>
                {!isEditingIdentity ? (
                  <button
                    type="button"
                    onClick={() => { setIdentityError(''); setIsEditingIdentity(true); }}
                    aria-label="Edit shared contact details"
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm hover:bg-primary-50"
                  >
                    <Edit2 aria-hidden="true" className="h-3.5 w-3.5" /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setIdentityError(''); setIsEditingIdentity(false); }}
                      disabled={savingIdentity}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveIdentity}
                      disabled={savingIdentity}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      {savingIdentity ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              {isEditingIdentity && <p className="text-sm text-gray-600 mb-3">Changes apply to this person across all their enquiries.</p>}
              {identityError && (
                <FormError message={identityError} />
              )}
              {isEditingIdentity ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <label htmlFor="detail-name" className="block text-xs font-medium text-gray-600 mb-0.5">Name</label>
                    <input id="detail-name" type="text" required disabled={savingIdentity}
                      value={identityDraft.name}
                      onChange={(e) => setIdentityDraft({ ...identityDraft, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label htmlFor="detail-phone" className="block text-xs font-medium text-gray-600 mb-0.5">Phone number</label>
                    <input id="detail-phone" type="tel" disabled={savingIdentity}
                      value={identityDraft.phoneNumber}
                      onChange={(e) => setIdentityDraft({ ...identityDraft, phoneNumber: e.target.value })}
                      aria-describedby="detail-phone-help"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" />
                    <p id="detail-phone-help" className="text-xs text-gray-500 mt-1">Use a country code, e.g. +263771234567. Local numbers use Zimbabwe.</p>
                  </div>
                  <div>
                    <label htmlFor="detail-email" className="block text-xs font-medium text-gray-600 mb-0.5">Email</label>
                    <input
                      id="detail-email"
                      type="email"
                      value={identityDraft.email}
                      onChange={(e) => setIdentityDraft({ ...identityDraft, email: e.target.value })}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="detail-social" className="block text-xs font-medium text-gray-600 mb-0.5">Social handle</label>
                    <input id="detail-social" type="text" maxLength={300} disabled={savingIdentity}
                      value={identityDraft.socialHandle}
                      onChange={(e) => setIdentityDraft({ ...identityDraft, socialHandle: e.target.value })}
                      placeholder="e.g., Instagram: @jane"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label htmlFor="detail-source" className="block text-xs font-medium text-gray-600 mb-0.5">Source</label>
                    <select
                      id="detail-source"
                      value={identityDraft.source}
                      onChange={(e) => setIdentityDraft({ ...identityDraft, source: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  {identityDraft.source === 'other' && <div><label htmlFor="detail-other-source" className="block text-xs font-medium text-gray-600 mb-0.5">Specify source</label><input id="detail-other-source" type="text" value={identityDraft.otherSource || ''} onChange={event=>setIdentityDraft({...identityDraft,otherSource:event.target.value})} maxLength={120} placeholder="e.g. Google search, an event, a flyer" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"/></div>}
                  {identityDraft.source === 'referral' && (
                    <div>
                      <label htmlFor="detail-referrer-name" className="block text-xs font-medium text-gray-600 mb-0.5">Referrer name</label>
                      <input
                        id="detail-referrer-name"
                        type="text"
                        value={identityDraft.referrerName}
                        onChange={(e) => setIdentityDraft({ ...identityDraft, referrerName: e.target.value })}
                        placeholder="Who referred?"
                        maxLength={120}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="flex min-w-0 items-center gap-2.5 rounded-lg bg-gray-50 p-3">
                    <UserRound aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Name</dt><dd className="truncate text-sm font-semibold text-gray-900">{lead.name || 'Not recorded'}</dd></span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2.5 rounded-lg bg-gray-50 p-3">
                    <Phone aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Phone</dt><dd className="truncate text-sm font-semibold text-gray-900">{lead.phoneNumber || 'Not recorded'}</dd></span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2.5 rounded-lg bg-gray-50 p-3">
                    <Mail aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Email</dt><dd className="truncate text-sm font-semibold text-gray-900">{lead.email || 'Not recorded'}</dd></span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2.5 rounded-lg bg-gray-50 p-3">
                    <MessageCircle aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="min-w-0"><dt className="text-xs font-medium text-gray-500">Social handle</dt><dd className="truncate text-sm font-semibold text-gray-900">{lead.socialHandle || 'Not recorded'}</dd></span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500">Source</dt>
                    <dd className="mt-0.5 text-sm font-semibold text-gray-900">{lead.source ? `${SOURCE_OPTIONS.find((o) => o.value === lead.source)?.label || lead.source}${lead.source === 'other' && lead.otherSource ? ` · ${lead.otherSource}` : ''}${lead.source === 'referral' && lead.referrerName ? ` · ${lead.referrerName}` : ''}` : 'Not recorded'}</dd>
                  </div>
                </dl>
              )
              }
            </div>
          )}

          <LeadIndustryDetails key={`industry-${lead.id}`} lead={lead} canEdit={isOwner} />
          <LeadDeal key={lead.id} lead={lead} canEdit={can(user, 'editEnquiries') && (isOwner || lead.assignedUserId === user?.id)} />
          {/* Intent (editable by owner only per RULES) */}
          {isOwner && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span><span className="block text-sm font-semibold text-gray-900">Enquiry details</span><span className="mt-0.5 block text-xs text-gray-500">Product interest, budget and buying timeframe</span></span>
                {!isEditingIntent ? (
                  <button
                    type="button"
                    onClick={() => setIsEditingIntent(true)}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm hover:bg-primary-50"
                  >
                    <Edit2 aria-hidden="true" className="h-3.5 w-3.5" /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditingIntent(false)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveIntent}
                      disabled={savingIntent}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      {savingIntent ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              {intentError && (
                <p className="text-sm text-red-600 mb-2">{intentError}</p>
              )}
              {isEditingIntent ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="sm:col-span-2"><EnquiryInterest value={intentDraft} onChange={setIntentDraft}/></div>
                  <div>
                    <label htmlFor="detail-budget-range" className="block text-xs font-medium text-gray-600 mb-0.5">Budget range</label>
                    <div className="relative">
                      <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        id="detail-budget-range"
                        type="text"
                        value={intentDraft.budgetRange}
                        onChange={(e) => setIntentDraft({ ...intentDraft, budgetRange: e.target.value })}
                        placeholder="Optional"
                        maxLength={120}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="detail-urgency" className="block text-xs font-medium text-gray-600 mb-0.5">Buying timeframe</label>
                    <div className="relative">
                      <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        id="detail-urgency"
                        value={intentDraft.urgency}
                        onChange={(e) => setIntentDraft({ ...intentDraft, urgency: e.target.value })}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Not set</option>
                        <option value="now">Now</option>
                        <option value="soon">Soon</option>
                        <option value="browsing">Just browsing</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm">
                  {!lead.productOrService && !lead.budgetRange && !lead.urgency && '—'}
                  {(lead.productOrService || lead.budgetRange || lead.urgency) && (
                    <dl className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {lead.productOrService && (
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                            <Package className="w-3.5 h-3.5" />
                          </span>
                          <div className="min-w-0">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Interested in</dt>
                            <dd className="text-gray-900 truncate">{lead.productOrService}</dd>
                          </div>
                        </div>
                      )}
                      {lead.budgetRange && (
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                            <Wallet className="w-3.5 h-3.5" />
                          </span>
                          <div className="min-w-0">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Budget</dt>
                            <dd className="text-gray-900 truncate">{lead.budgetRange}</dd>
                          </div>
                        </div>
                      )}
                      {lead.urgency && (
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                            <Zap className="w-3.5 h-3.5" />
                          </span>
                          <div className="min-w-0">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Buying timeframe</dt>
                            <dd className="text-gray-900 truncate">{lead.urgency.charAt(0).toUpperCase() + lead.urgency.slice(1)}</dd>
                          </div>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              )}
            </div>
          )}

          </DetailSection>
          <DetailSection id="enquiry-team" title="Team & actions" description="Assignment, watchers and deletion" icon={UserPlus}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                <UserPlus className="w-4 h-4" />
              </span>
              <span>
                {hasAiAccess(user) && lead.assignedAgentId ? 'Responsible AI agent' : 'Responsible person'}: <span className="font-semibold text-gray-900">{hasAiAccess(user) && lead.assignedAgentId ? `${teamAgents.find(a => a.id === lead.assignedAgentId)?.name || 'AI agent'} (AI)` : lead.assignedUser?.fullName || lead.assignedUser?.businessName || (lead.assignedUserId ? 'Team member' : 'Unassigned')}</span>
              </span>
            </div>
            {teamInboxEnabled && !lead.assignedUserId && !lead.assignedAgentId && (
              <button
                onClick={() => assignMutation.mutate(user?.id)}
                disabled={!user?.id || assignMutation.isPending}
                className="w-full sm:w-auto px-3 py-1.5 border border-primary-200 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Assign to me
              </button>
            )}
            {isOwner && (teamInboxEnabled || agentTeam?.enabled) && (
              <select
                aria-label={agentTeam?.enabled ? "Responsible teammate" : "Responsible person"}
                disabled={assignMutation.isPending}
                value={hasAiAccess(user) && lead.assignedAgentId ? `agent:${lead.assignedAgentId}` : lead.assignedUserId || ''}
                onChange={(e) => assignMutation.mutate(e.target.value || null)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="">Unassigned</option>
                {teamAgents.map(agent => <option key={agent.id} value={`agent:${agent.id}`} disabled={!agent.active}>{agent.name} (AI agent{agent.active ? '' : ' · Paused'})</option>)}
                {assignmentMembers.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.fullName || member.email || member.userId}
                    {member.role === 'owner' ? ' (Owner)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Created by: <span className="font-semibold text-gray-900">{lead.createdBy?.fullName || lead.createdBy?.businessName || (lead.createdByUserId ? 'Former team member' : 'Not recorded')}</span>

          </p>
          {assignMutation.isError && <FormError message={assignMutation.error?.response?.data?.error || 'Could not change the responsible person. Please try again.'} />}

          {/* Watchers */}
          {teamInboxEnabled && (
            <div className="flex flex-wrap items-center gap-2 mb-5 text-sm text-gray-600">
              <span className="font-medium text-gray-700">Watching:</span>
              {watchers.length > 0 ? (
                watchers.map((w) => (
                  <span key={w.userId} className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                    {w.fullName || 'Team member'}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 text-xs">No watchers</span>
              )}
              <button
                onClick={() => isWatching ? unwatchMutation.mutate() : watchMutation.mutate()}
                disabled={watchMutation.isPending || unwatchMutation.isPending}
                className={`ml-auto px-3 py-1 rounded-lg text-xs font-medium transition-colors border ${isWatching
                    ? 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                    : 'border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {isWatching ? 'Unwatch' : 'Watch'}
              </button>
            </div>
          )}

            <button
              ref={deleteTriggerRef}
              aria-label="Delete enquiry"
              aria-expanded={confirmDelete}
              aria-controls="delete-enquiry-confirmation"
              disabled={deleteLeadMutation.isPending}
              onClick={() => { deleteLeadMutation.reset(); setConfirmDelete(true); }}
              className="min-h-[44px] inline-flex items-center gap-2 mb-3 p-2 text-gray-500 hover:text-danger-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200 hover:border-red-200"
            >
              <Trash2 className="w-4 h-4" />
              Delete enquiry
            </button>
            {confirmDelete && <section id="delete-enquiry-confirmation" role="group" aria-label="Confirm enquiry deletion"
              onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); cancelDelete(); } }}
              className="mb-4 space-y-3 rounded-xl border border-red-200 bg-red-50 p-4">
              <h3 className="font-semibold text-red-900">Delete this enquiry permanently?</h3>
              <p className="text-sm text-red-900">Delete “{lead.title || lead.productOrService || lead.name || 'Sales enquiry'}”? This cannot be undone. Choose Lost instead if you want to keep the sales history.</p>
              {deleteLeadMutation.isError && <p role="alert" className="text-sm font-medium text-red-800">{deleteLeadMutation.error?.response?.data?.error || 'Could not delete the enquiry. Please try again.'}</p>}
              {deleteLeadMutation.isPending && <p role="status" className="text-sm text-red-900">Deleting enquiry…</p>}
              <div className="flex flex-wrap gap-3">
                <button ref={deleteCancelRef} type="button" disabled={deleteLeadMutation.isPending} onClick={cancelDelete} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50">Keep enquiry</button>
                <button type="button" disabled={deleteLeadMutation.isPending} onClick={() => { if (!deleteLeadMutation.isPending) deleteLeadMutation.mutate(); }} className="rounded-lg bg-danger-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{deleteLeadMutation.isPending ? 'Deleting…' : 'Permanently delete'}</button>
              </div>
            </section>}
          </DetailSection>
          <DetailSection id="enquiry-conversation" title="Conversation" description="Tags and recent contact" icon={MessageCircle}>
          {/* WhatsApp Context */}
          <div className="pb-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ConversationTags key={lead.id} lead={lead} canEdit={isOwner} />

              <div className="space-y-4">
                <div className="rounded-lg bg-gray-50 p-4 border border-gray-100 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-whatsapp-500/10 text-whatsapp-600">
                      <Clock className="w-4 h-4 shrink-0" />
                    </span>
                    Last WhatsApp contact
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {lastContactLabel}
                      </div>
                      {lastContactExact && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {lastContactExact}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleMarkContactedToday}
                      disabled={markingContacted}
                      className="shrink-0 w-full sm:w-auto px-3 py-1.5 bg-whatsapp-500 text-white rounded-lg text-sm font-medium hover:bg-whatsapp-600 disabled:opacity-50 shadow-sm"
                    >
                      {markingContacted ? 'Logging...' : 'Log contact'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Connected inbox messages log contact automatically once sent, when the conversation is linked to this enquiry. Use Log contact for chats outside the inbox.
                  </p>
                  {contactConfirmation && (
                    <p role="status" aria-live="polite" className="text-sm font-medium text-green-700">{contactConfirmation}</p>
                  )}
                </div>

                <div className="rounded-lg bg-gray-50 p-4 border border-gray-100 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <MessageCircle className="w-4 h-4 shrink-0" />
                    </span>
                    Last message summary
                  </div>
                  <textarea
                    value={lastMessageSummaryDraft}
                    onChange={(e) => {
                      setLastMessageSummaryDraft(e.target.value);
                      setIsLastMessageSummaryDirty(true);
                    }}
                    maxLength={500}
                    rows={3}
                    aria-label="Last message summary"
                    placeholder="Summarize the latest conversation (optional)"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                  />
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{lastMessageSummaryDraft.length}/500</span>
                    <button
                      onClick={() => handleSaveLastMessageSummary(lastMessageSummaryDraft)}
                      disabled={
                        savingLastMessageSummary ||
                        lastMessageSummaryDraft === (lead.lastMessageSummary || '')
                      }
                      className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingLastMessageSummary ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {lastMessageSummaryError && (
                    <FormError message={lastMessageSummaryError} />
                  )}
                </div>
              </div>
            </div>

            {whatsAppContextError && (
              <FormError message={whatsAppContextError} />
            )}
          </div>
          </DetailSection>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 p-1 rounded-lg bg-gray-100 border border-gray-200/80"
        role="tablist"
        aria-label="Enquiry detail sections"
        onKeyDown={handleTabKeyDown}
      >
        <button
          id="lead-tab-notes"
          ref={(node) => { tabRefs.current.notes = node; }}
          type="button"
          role="tab"
          aria-selected={activeTab === 'notes'}
          aria-controls="lead-panel-notes"
          tabIndex={activeTab === 'notes' ? 0 : -1}
          onClick={() => selectTab('notes')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'notes'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Internal Notes ({lead.notes?.length || 0})
        </button>
        <button
          id="lead-tab-followups"
          ref={(node) => { tabRefs.current.followups = node; }}
          type="button"
          role="tab"
          aria-selected={activeTab === 'followups'}
          aria-controls="lead-panel-followups"
          tabIndex={activeTab === 'followups' ? 0 : -1}
          onClick={() => selectTab('followups')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'followups'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Tasks
        </button>
        <button
          id="lead-tab-activity"
          ref={(node) => { tabRefs.current.activity = node; }}
          type="button"
          role="tab"
          aria-selected={activeTab === 'activity'}
          aria-controls="lead-panel-activity"
          tabIndex={activeTab === 'activity' ? 0 : -1}
          onClick={() => selectTab('activity')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'activity'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Activity
        </button>
      </div>

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div
          id="lead-panel-notes"
          role="tabpanel"
          aria-labelledby="lead-tab-notes"
          tabIndex={0}
          className="space-y-4 lg:space-y-5"
        >
          {/* Add Note */}
          <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
            <div className="relative">
              <textarea
                aria-label="Internal note"
                ref={noteTextareaRef}
                value={newNote}
                onChange={handleNoteChange}
                onKeyDown={handleNoteKeyDown}
                onFocus={() => startEditingHeartbeat('notes')}
                onBlur={() => { setTimeout(() => setMentionActive(false), 150); stopEditingHeartbeat(); }}
                placeholder={teamInboxEnabled ? 'Add a note... (type @ to mention someone)' : 'Add a note...'}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                rows={3}
              />
              {mentionActive && mentionFilteredMembers.length > 0 && (
                <div className="absolute left-0 right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                  {mentionFilteredMembers.map((member, idx) => (
                    <button
                      key={member.userId}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertMention(member);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${idx === mentionSelectedIdx ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                        {(member.fullName || member.email || '?')[0].toUpperCase()}
                      </span>
                      <span className="truncate">{member.fullName || member.email}</span>
                      {member.role === 'owner' && <span className="text-xs text-gray-400">(Owner)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || addingNote}
                className="bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50"
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>
            {deleteNoteMutation.isPending && <p role="status" className="text-sm text-gray-600">Deleting note…</p>}
            {noteError && <FormError message={noteError} />}
            {noteConfirmation && <p role="status" aria-live="polite" className="mt-2 text-sm font-medium text-green-700">{noteConfirmation}</p>}
          </div>

          {/* Notes List */}
          {lead.notes?.length === 0 ? (
            <div className="text-center py-10 text-gray-500 rounded-lg bg-gray-50 border border-gray-100">
              No internal notes yet. Add your first note above.
            </div>
          ) : (
            lead.notes?.map((note) => (
              <div key={note.id} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                {editingNote?.id === note.id ? (
                  <div className="space-y-2">
                    <textarea
                      disabled={!can(user, 'writeNotes') || updateNoteMutation.isPending}
                      value={editingNote.content}
                      onChange={(e) => setEditingNote({ id: note.id, content: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                      rows={3}
                      aria-label="Edit internal note"
                    />
                    {updateNoteMutation.isPending && <p role="status" className="text-sm text-gray-600">Saving note…</p>}
                    <div className="flex justify-end gap-2">
                      <button
                        aria-label="Cancel note edit"
                        disabled={!can(user, 'writeNotes') || updateNoteMutation.isPending}
                        onClick={() => setEditingNote(null)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        aria-label="Save note"
                        disabled={!can(user, 'writeNotes') || updateNoteMutation.isPending}
                        onClick={() => handleUpdateNote(note.id, editingNote.content)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {isWhatsAppNote(note) && <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" aria-label="Note source: WhatsApp, automatically captured">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-1 font-medium text-emerald-800"><MessageCircle aria-hidden="true" className="h-3.5 w-3.5" />WhatsApp</span>
                      <span className="text-gray-500">Auto-captured</span>
                    </div>}
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {(() => {
                        // Render @mentions as highlighted pills
                        const mentionRe = /@\[([^\]]+)\]\(([^)]+)\)/gi;
                        const parts = [];
                        let lastIdx = 0;
                        let m;
                        let matchCount = 0;
                        const text = noteText(note);
                        while ((m = mentionRe.exec(text)) !== null) {
                          if (m.index > lastIdx) {
                            parts.push(text.slice(lastIdx, m.index));
                          }
                          const name = m[1].trim();
                          parts.push(
                            <span key={`mention-${note.id}-${matchCount++}`} className="inline-flex items-center align-middle px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold mx-0.5 border border-primary-200 shadow-sm">
                              @{name}
                            </span>
                          );
                          lastIdx = m.index + m[0].length;
                        }
                        if (lastIdx < text.length) {
                          parts.push(text.slice(lastIdx));
                        }
                        return parts.length > 0 ? parts : text;
                      })()}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        {!isWhatsAppNote(note) && <>{note.createdBy?.fullName || note.createdBy?.businessName || 'Team member'}{' · '}</>}
                        {format(parseISO(note.createdAt), 'MMM d, yyyy h:mm a')}
                      </span>
                      <div className="flex gap-1">
                        <button
                          aria-label="Edit note"
                          disabled={!can(user, 'writeNotes')}
                          onClick={() => setEditingNote({ id: note.id, content: noteText(note) })}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          aria-label="Delete note"
                          disabled={!can(user, 'writeNotes')}
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Follow-ups Tab */}
      {activeTab === 'followups' && (
        <div
          id="lead-panel-followups"
          role="tabpanel"
          aria-labelledby="lead-tab-followups"
          tabIndex={-1}
          ref={followUpsPanelRef}
          className="space-y-3 outline-none"
        >
          {followUpConfirmation && <p role="status" aria-live="polite" className="text-sm font-medium text-green-700">{followUpConfirmation}</p>}
          {/* Add Follow-up Button */}
          {!showAddFollowUp && (
            <button
              disabled={!can(user, 'manageFollowUps')}
              onClick={() => setShowAddFollowUp(true)}
              className="w-full flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-200 text-gray-500 py-4 rounded-lg font-medium hover:border-primary-300 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add task
            </button>
          )}

          {/* Add Follow-up Form */}
          {showAddFollowUp && (
            <FollowUpForm onCancel={() => setShowAddFollowUp(false)} onSubmit={async (draft) => {
              await addFollowUpMutation.mutateAsync({ leadId: id, ...draft });
              setShowAddFollowUp(false);
              setFollowUpConfirmation('Task added.');
            }} />
          )}

          {loadingFollowUps && <p role="status" className="text-gray-500">Loading tasks…</p>}
          {followUpsError && <FormError message="Could not load task history. Please try again." />}
          {['open','completed','cancelled'].map((taskStatus) => {
            const completed=taskStatus==='completed';
            const items = followUpHistory.filter((item) => (item.status || (item.cancelled?'cancelled':item.completed?'completed':'open')) === taskStatus);
            if (!items.length) return null;
            return (
              <section key={taskStatus} className="space-y-3" aria-label={`${taskStatus} tasks`}>
                <h3 className="font-semibold text-gray-900">{taskStatus==='cancelled'?'Cancelled':completed?'Completed':'Open'} ({items.length})</h3>
                {items.map((followUp) => (
                  <div key={followUp.id} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    <TaskSource task={followUp}/>
                    <p className="text-gray-600">{formatFollowUp(followUp)}</p>
                    {followUp.note && <p className="text-sm text-gray-500 mt-2 bg-gray-50 rounded p-2">{followUp.note}</p>}
                    {taskStatus==='cancelled' ? <p className="mt-2 text-sm text-gray-500">Cancelled{followUp.cancelReason?`: ${followUp.cancelReason}`:''}</p> : completed
                      ? <p className="mt-2 text-sm font-medium text-green-700">Completed</p>
                      : <FollowUpActions followUp={followUp} onSuccess={setFollowUpConfirmation} />}
                  </div>
                ))}
              </section>
            );
          })}
          {!loadingFollowUps && !followUpsError && followUpHistory.length === 0 && !showAddFollowUp && (
            <div className="text-center py-8 text-gray-500">No tasks yet. Add one above.</div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div
          id="lead-panel-activity"
          role="tabpanel"
          aria-labelledby="lead-tab-activity"
          tabIndex={0}
          className="space-y-3"
        >
          {activityLog.length === 0 ? (
            <div className="text-center py-10 text-gray-500 rounded-lg bg-gray-50 border border-gray-100">
              No activity yet.
            </div>
          ) : (
            activityLog.map((entry) => (
              <div key={entry.id} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                <div className="text-sm text-gray-700 font-medium">
                  {getActivityLabel(entry)}
                  {getActivityMeta(entry) && (
                    <span className="text-gray-500 font-normal"> · {getActivityMeta(entry)}</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {entry.user?.fullName || 'Someone'} • {format(parseISO(entry.createdAt), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LeadDetail;
